import React, { useRef, useState } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './BlogSection.css';

// Image protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const blogPosts = [
    {
        id: 1,
        title: "The Art of Storytelling in Wedding Films",
        category: "INSIGHTS",
        date: "OCT 24, 2023",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752464/DSC_7786-Edited_copy_jcmquq.webp",
        excerpt: "Capturing the raw emotions and fleeting moments that make your special day truly unforgettable."
    },
    {
        id: 2,
        title: "Top 10 Destinations for Pre-Wedding Shoots",
        category: "LOCATIONS",
        date: "NOV 15, 2023",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754992/J_kas1gy.webp",
        excerpt: "From the cliffs of Amalfi to the deserts of Dubai, discover the perfect backdrop for your love story."
    },
    {
        id: 3,
        title: "Why You Need a Cinematic Wedding Video",
        category: "GUIDE",
        date: "DEC 02, 2023",
        image: "https://res.cloudinary.com/dhqmcmekn/image/upload/v1764752454/18_djwvqd.webp",
        excerpt: "More than just a recording, a cinematic film preserves the atmosphere and feeling of your wedding."
    }
];

const BlogSection: React.FC = () => {
    const navigate = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ["start end", "end start"]
    });

    const headerY = useTransform(scrollYProgress, [0.1, 0.3], [100, 0]);
    const headerOpacity = useTransform(scrollYProgress, [0.1, 0.3], [0, 1]);

    const handleViewAll = () => {
        navigate('/journal');
    };

    const handleReadStory = (id: number) => {
        navigate('/journal', { state: { postId: id } });
    };

    // Mobile Carousel Logic
    const nextCard = () => {
        setCurrentIndex((prev) => (prev + 1) % blogPosts.length);
    };

    const prevCard = () => {
        setCurrentIndex((prev) => (prev - 1 + blogPosts.length) % blogPosts.length);
    };

    const minSwipeDistance = 50;

    const onTouchStart = (e: React.TouchEvent) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe) nextCard();
        if (isRightSwipe) prevCard();
    };

    return (
        <section className="blog-section" id="blog" ref={containerRef}>
            <div className="blog-container">
                <motion.div
                    className="blog-header"
                    style={{ y: headerY, opacity: headerOpacity }}
                >
                    <div className="header-content">
                        <h2 className="section-title">LATEST</h2>
                        <h2 className="section-title indent">
                            <span className="italic-accent">from the</span> JOURNAL
                        </h2>
                    </div>
                    <motion.button
                        className="view-all-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleViewAll}
                    >
                        VIEW ALL STORIES
                    </motion.button>
                </motion.div>

                {/* Desktop Layout */}
                <div className="blog-grid desktop-view">
                    <motion.div
                        className="featured-post"
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                    >
                        <BlogCard post={blogPosts[0]} isFeatured={true} onRead={() => handleReadStory(blogPosts[0].id)} />
                    </motion.div>
                    <div className="side-posts">
                        {blogPosts.slice(1).map((post, index) => (
                            <motion.div
                                key={post.id}
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8, delay: index * 0.2 }}
                            >
                                <BlogCard post={post} isFeatured={false} onRead={() => handleReadStory(post.id)} />
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Mobile Layout */}
                <div className="mobile-view">
                    <div className="blog-carousel">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={currentIndex}
                                initial={{ opacity: 0, x: 50 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -50 }}
                                transition={{ duration: 0.4 }}
                                className="carousel-item"
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                            >
                                <BlogCard post={blogPosts[currentIndex]} isFeatured={true} onRead={() => handleReadStory(blogPosts[currentIndex].id)} />
                            </motion.div>
                        </AnimatePresence>

                        <div className="carousel-controls">
                            <button className="control-btn" onClick={prevCard}>←</button>
                            <div className="indicators">
                                {blogPosts.map((_, idx) => (
                                    <div
                                        key={idx}
                                        className={`indicator ${idx === currentIndex ? 'active' : ''}`}
                                        onClick={() => setCurrentIndex(idx)}
                                    />
                                ))}
                            </div>
                            <button className="control-btn" onClick={nextCard}>→</button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const BlogCard: React.FC<{ post: typeof blogPosts[0], isFeatured: boolean, onRead: () => void }> = ({ post, isFeatured, onRead }) => {
    return (
        <div className={`blog-card ${isFeatured ? 'featured' : 'standard'}`} onClick={onRead}>
            <div className="image-wrapper">
                <img 
                    src={post.image} 
                    alt={post.title}
                    onContextMenu={handleContextMenu}
                    onDragStart={handleDragStart}
                    style={{ pointerEvents: 'none' }}
                />
                <div className="category-tag">{post.category}</div>
            </div>
            <div className="content-wrapper">
                <div className="meta-info">
                    <span className="date">{post.date}</span>
                    <span className="separator">•</span>
                    <span className="read-time">5 MIN READ</span>
                </div>
                <h3 className="post-title">{post.title}</h3>
                {isFeatured && <p className="post-excerpt">{post.excerpt}</p>}
                <div className="read-more">
                    READ STORY <span className="arrow">→</span>
                </div>
            </div>
        </div>
    );
};

export default BlogSection;
